import React from 'react';
import styles from './AvailabilityTable.module.scss';

import { NoDataTableRow, TableWrapper } from 'components/SummaryComponents';
import { StyledTableCell, StyledTableRow } from 'components/StyledComponents';
import StyledLink from 'components/StyledLink';

import { Table, TableHead, TableBody } from '@material-ui/core';

import { useUserConfig } from 'hooks/useUserConfig';

import clsx from 'clsx';

import { generateCellUrl } from '../../../../helpers';

const EmptyTable = () => (
  <div className={styles.table}>
    <TableWrapper>
      <Table>
        <TableHead>
          <StyledTableRow>
            <StyledTableCell>Status</StyledTableCell>
            <StyledTableCell>Staff Sourcing</StyledTableCell>
            <StyledTableCell></StyledTableCell>
            <StyledTableCell></StyledTableCell>
          </StyledTableRow>
        </TableHead>
        <TableBody>
          <NoDataTableRow isTableFiltered={false} />
        </TableBody>
      </Table>
    </TableWrapper>
  </div>
);

export default function AvailabilityTable({ dashboard, dashboardId, department }) {
  if (!('total' in dashboard)) return <EmptyTable />;

  const {
    available,
    availableNotSubmitted,
    pto,
    rr,
    total,
    totalUnavailable,
    unavailable,
    undeterminedData,
    duration1ColumnName,
    duration2ColumnName
  } = dashboard;

  const { isAdminUser, isPersonnelManagerUser } = useUserConfig();

  const COLUMNS = [
    { id: 1, label: 'Status' },
    { id: 2, label: 'Staff Sourcing' },
    { id: 3, label: duration1ColumnName },
    { id: 4, label: duration2ColumnName }
  ];

  const generateUrlParams = (options) => ({
    dashboardId: dashboardId,
    dashboardType: 'StaffingLevelsAndAvailability',
    ...options
  });

  const shouldAllowRedirect = (value = 0) => (isAdminUser || isPersonnelManagerUser) && value > 0;

  return (
    <div className={styles.table}>
      <TableWrapper>
        <Table>
          <TableHead>
            <StyledTableRow>
              {COLUMNS.map(({ id, label }) => (
                <StyledTableCell key={id}>{label}</StyledTableCell>
              ))}
            </StyledTableRow>
          </TableHead>
          <TableBody>
            {total?.duration1Value > 0 || total?.duration2Value > 0 ? (
              <>
                <StyledTableRow>
                  <StyledTableCell className={clsx(styles.statusCell, styles.extended)} rowSpan={4}>
                    Unavailable
                  </StyledTableCell>
                  <StyledTableCell className={styles.staffCell}>PTO</StyledTableCell>
                  <StyledTableCell className={styles.durationCell}>
                    <StyledLink
                      to={generateCellUrl(
                        generateUrlParams({ dashboardDuration: 1, dashboardField: 'PTO' })
                      )}
                      isRedirectAvailable={shouldAllowRedirect(pto.duration1Value)}>
                      {pto.duration1Value || 0}
                    </StyledLink>
                  </StyledTableCell>
                  <StyledTableCell className={styles.durationCell}>
                    <StyledLink
                      to={generateCellUrl(
                        generateUrlParams({ dashboardDuration: 2, dashboardField: 'PTO' })
                      )}
                      isRedirectAvailable={shouldAllowRedirect(pto.duration2Value)}>
                      {pto.duration2Value || 0}
                    </StyledLink>
                  </StyledTableCell>
                </StyledTableRow>
                <StyledTableRow>
                  <StyledTableCell className={styles.staffCell}>R&R, RR</StyledTableCell>
                  <StyledTableCell className={styles.durationCell}>
                    <StyledLink
                      to={generateCellUrl(
                        generateUrlParams({ dashboardDuration: 1, dashboardField: 'RR' })
                      )}
                      isRedirectAvailable={shouldAllowRedirect(rr.duration1Value)}>
                      {rr.duration1Value || 0}
                    </StyledLink>
                  </StyledTableCell>
                  <StyledTableCell className={styles.durationCell}>
                    <StyledLink
                      to={generateCellUrl(
                        generateUrlParams({ dashboardDuration: 2, dashboardField: 'RR' })
                      )}
                      isRedirectAvailable={shouldAllowRedirect(rr.duration2Value)}>
                      {rr.duration2Value || 0}
                    </StyledLink>
                  </StyledTableCell>
                </StyledTableRow>
                <StyledTableRow>
                  <StyledTableCell className={styles.staffCell}>Unavailable</StyledTableCell>
                  <StyledTableCell className={styles.durationCell}>
                    <StyledLink
                      to={generateCellUrl(
                        generateUrlParams({ dashboardDuration: 1, dashboardField: 'Unavailable' })
                      )}
                      isRedirectAvailable={shouldAllowRedirect(unavailable.duration1Value)}>
                      {unavailable.duration1Value || 0}
                    </StyledLink>
                  </StyledTableCell>
                  <StyledTableCell className={styles.durationCell}>
                    <StyledLink
                      to={generateCellUrl(
                        generateUrlParams({ dashboardDuration: 2, dashboardField: 'Unavailable' })
                      )}
                      isRedirectAvailable={shouldAllowRedirect(unavailable.duration2Value)}>
                      {unavailable.duration2Value || 0}
                    </StyledLink>
                  </StyledTableCell>
                </StyledTableRow>
                <StyledTableRow>
                  <StyledTableCell className={styles.staffCell}>Undetermined Data</StyledTableCell>
                  <StyledTableCell className={styles.durationCell}>
                    <StyledLink
                      to={generateCellUrl(
                        generateUrlParams({
                          dashboardDuration: 1,
                          dashboardField: 'UndeterminedData'
                        })
                      )}
                      isRedirectAvailable={shouldAllowRedirect(undeterminedData.duration1Value)}>
                      {undeterminedData.duration1Value || 0}
                    </StyledLink>
                  </StyledTableCell>
                  <StyledTableCell className={styles.durationCell}>
                    <StyledLink
                      to={generateCellUrl(
                        generateUrlParams({
                          dashboardDuration: 2,
                          dashboardField: 'UndeterminedData'
                        })
                      )}
                      isRedirectAvailable={shouldAllowRedirect(undeterminedData.duration2Value)}>
                      {undeterminedData.duration2Value || 0}
                    </StyledLink>
                  </StyledTableCell>
                </StyledTableRow>

                <StyledTableRow className={styles.unavailable}>
                  <StyledTableCell className={styles.statusCell}>Total Unavailable</StyledTableCell>
                  <StyledTableCell className={styles.staffCell}></StyledTableCell>
                  <StyledTableCell className={clsx(styles.durationCell, styles.bold)}>
                    {totalUnavailable.duration1Value || 0}
                  </StyledTableCell>
                  <StyledTableCell className={clsx(styles.durationCell, styles.bold)}>
                    {totalUnavailable.duration2Value || 0}
                  </StyledTableCell>
                </StyledTableRow>

                <StyledTableRow className={styles.available}>
                  <StyledTableCell className={styles.statusCell}>Available</StyledTableCell>
                  <StyledTableCell className={styles.staffCell}></StyledTableCell>
                  <StyledTableCell className={clsx(styles.durationCell, styles.bold)}>
                    <StyledLink
                      to={generateCellUrl(
                        generateUrlParams({
                          dashboardDuration: 1,
                          dashboardField: 'Available'
                        })
                      )}
                      isRedirectAvailable={shouldAllowRedirect(available.duration1Value)}>
                      {available.duration1Value || 0}
                    </StyledLink>
                  </StyledTableCell>
                  <StyledTableCell className={clsx(styles.durationCell, styles.bold)}>
                    <StyledLink
                      to={generateCellUrl(
                        generateUrlParams({
                          dashboardDuration: 2,
                          dashboardField: 'Available'
                        })
                      )}
                      isRedirectAvailable={shouldAllowRedirect(available.duration2Value)}>
                      {available.duration2Value || 0}
                    </StyledLink>
                  </StyledTableCell>
                </StyledTableRow>

                <StyledTableRow className={styles.notSubmitted}>
                  <StyledTableCell className={styles.statusCell}>
                    Availability Not Submitted
                  </StyledTableCell>
                  <StyledTableCell className={styles.staffCell}></StyledTableCell>
                  <StyledTableCell className={clsx(styles.durationCell, styles.bold)}>
                    <StyledLink
                      to={generateCellUrl(
                        generateUrlParams({
                          dashboardDuration: 1,
                          dashboardField: 'AvailableNotSubmitted'
                        })
                      )}
                      isRedirectAvailable={shouldAllowRedirect(
                        availableNotSubmitted.duration1Value
                      )}>
                      {availableNotSubmitted.duration1Value || 0}
                    </StyledLink>
                  </StyledTableCell>
                  <StyledTableCell className={clsx(styles.durationCell, styles.bold)}>
                    <StyledLink
                      to={generateCellUrl(
                        generateUrlParams({
                          dashboardDuration: 2,
                          dashboardField: 'AvailableNotSubmitted'
                        })
                      )}
                      isRedirectAvailable={shouldAllowRedirect(
                        availableNotSubmitted.duration2Value
                      )}>
                      {availableNotSubmitted.duration2Value || 0}
                    </StyledLink>
                  </StyledTableCell>
                </StyledTableRow>

                <StyledTableRow>
                  <StyledTableCell className={clsx(styles.statusCell, styles.bold)}>
                    Total
                  </StyledTableCell>
                  <StyledTableCell className={styles.staffCell}></StyledTableCell>
                  <StyledTableCell className={styles.durationCell}>
                    {total.duration1Value || 0}
                  </StyledTableCell>
                  <StyledTableCell className={styles.durationCell}>
                    {total.duration2Value || 0}
                  </StyledTableCell>
                </StyledTableRow>
              </>
            ) : (
              <NoDataTableRow isTableFiltered={!!department?.id} />
            )}
          </TableBody>
        </Table>
      </TableWrapper>
    </div>
  );
}
